import React, { useState, useEffect } from "react";
import {
  Table,
  Empty,
  Row,
  Drawer,
  Col,
  Modal,
  Divider,
  Button,
  notification,
  Checkbox,
} from "antd";

import {
  InfoCircleOutlined,
  BarsOutlined,
  SendOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import RequestHandler from "../../util/RequestHandler";
import moment from "moment";

import BkmPaymentAddForm from "../Forms/BkmPaymentAddForm";
import BkmPaymentTransactionTableFilter from "../Filters/BkmPaymentTransactionTableFilter";
import { ColumnHelper, RowHelper } from "../../util/TableHelper";
import { useHistory } from "react-router-dom";

const BkmPaymentTransaction = (props) => {
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState({
    skip: 0,
    take: 15,
    current: 1,
  });

  const [addFormVisibility, setAddFormVisibility] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [detailModal, setDetailModal] = useState({ visible: false });
  const [saveAccountData, setSaveAccountData] = useState({});

  const history = useHistory();

  useEffect(() => getTableData(), [tableDataQueryOptions]);

  useEffect(() => {
    if (props.addButtonEvent > 0) {
      setAddFormVisibility(true);
    }
  }, [props.addButtonEvent]);

  useEffect(() => {
    const result = checkIsBkmComplete();
    if (result.isBkmComplete) {
      getDetailModalData(result.paymentId, result);
    }
  }, []);

  const getFilterQuery = () => {
    const { filter } = tableDataQueryOptions;

    const conditions = [];
    let query = "";

    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length !== 0) {
      query = "&$filter=" + conditions.join(" and ");
    }
    return query;
  };

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });

    const { skip, take } = tableDataQueryOptions;

    RequestHandler.get(
      `/BkmPaymentTransaction?$count=true${getFilterQuery()}&$skip=${skip}&$top=${take}`,
    ).then((response) => {
      setTableData({
        data: response.value,
        total: response["@odata.count"],
        loading: false,
      });
    });
  };

  const checkIsBkmComplete = () => {
    const params = new URLSearchParams(window ? window.location.search : {});
    const isBkmComplete = params.get("isObComplete")?.toLowerCase() === "true";

    if (isBkmComplete) {
      const notificationType =
        params.get("success")?.toLowerCase() === "true" ? "success" : "error";
      notification[notificationType]({
        message: params.get("message"),
        duration: 5,
      });
      history.replace(window.location.pathname);

      return {
        isBkmComplete: isBkmComplete,
        paymentId: params.get("paymentId"),
        success: params.get("success")?.toLowerCase() === "true",
        message: params.get("message"),
        isSenderSaved: params.get("isSenderSaved")?.toLowerCase() === "true",
        isRecipientSaved:
          params.get("isRecipientSaved")?.toLowerCase() === "true",
      };
    }

    return { isBkmComplete: isBkmComplete };
  };

  const getAddFormData = (id) => {
    RequestHandler.get(`/BkmPaymentTransaction/GetById/${id}`).then(
      (response) => {
        if (response) {
          setAddFormData(response);
          setAddFormVisibility(true);
        }
      },
    );

    setAddFormVisibility(true);
  };

  const getDetailModalData = (id, bkmConsentResult) => {
    RequestHandler.get(`/BkmPaymentTransaction/GetById/${id}`).then(
      (response) => {
        if (response) {
          setDetailModal({
            ...response,
            bkmConsentResult: bkmConsentResult,
            visible: true,
          });
          if (bkmConsentResult) {
            setSaveAccountData({ id: bkmConsentResult.paymentId });
          }
        }
      },
    );
  };

  const paging = (p) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
      };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      skip: 0,
      current: 1,
      take: tableDataQueryOptions.take,
      filter: "",
    });
  };

  const onFinishTableFilter = (query) => {
    setTableDataQueryOptions((prevState) => {
      return { skip: 0, current: 1, take: prevState.take, filter: query };
    });
  };

  const onAddFormSuccess = () => {
    getTableData();
    setAddFormVisibility(false);
  };

  const getIdentityType = (type) => {
    const identityTypes = {
      V: "Vergi No",
      K: "T.C. Kimlik No",
      M: "Müşteri No",
      Y: "Yabancı Kimlik No",
      P: "Pasaport No",
    };

    return identityTypes[type];
  };

  const getPaymentPurpose = (type) => {
    const paymentPurposes = {
      "01": "Konut Kirası Ödemesi",
      "02": "İş yeri Kirası Ödemesi",
      "03": "Diğer Kira Ödemesi",
      "04": "E-Ticaret Ödemesi",
      "05": "Çalışan Ödemesi",
      "06": "Ticari ödeme",
      "07": "Bireysel Ödeme",
      "08": "Yatırım",
      "09": "Finansal ödemeler",
      10: "Eğitim ödemesi",
      11: "Aidat ödemesi",
      12: "Gayrimenkul alım ödemesi",
      13: "Motorlu taşıt alım ödemesi",
      14: "Borç verme/Borç ödeme",
      15: "Hediye/bağış/yardım",
      16: "Vergi/resim/harç ödemesi",
      17: "Tazminat/sigorta ödemesi",
      18: "Avukatlık/Danışmanlık/Müşavirlik Ödemesi",
      19: "Sağlık ödemesi",
      20: "Kripto/dijital varlık",
      21: "Şans oyunları/bahis ödemesi",
      22: "Eğlence/sosyal medya ödemesi",
    };

    return paymentPurposes[type] || type;
  };

  const columns = [
    {
      title: "Ödeme Hesap Sağlayıcı",
      dataIndex: "bankLogo",
      render: (data) => <img style={{ width: 120 }} src={data}></img>,
    },
    {
      title: "İşlem Tarihi",
      dataIndex: "addDate",
      align: "center",
      render: (data) => moment(data).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "Gönderen Iban",
      dataIndex: "senderIban",
    },
    {
      title: "Alıcı Ünvan",
      dataIndex: "recipientName",
    },
    {
      title: "Alıcı Iban",
      dataIndex: "recipientIban",
    },
    {
      title: "Para Birimi",
      dataIndex: "paymentCurrencyCode",
      align: "center",
    },
    {
      title: "Tutar",
      dataIndex: "paymentAmount",
      align: "center",
      render: (data) => (
        <div style={{ textAlign: "right" }}>
          {ColumnHelper.renderDecimal(data)}
        </div>
      ),
    },
    {
      title: "Ödeme Durumu",
      align: "center",
      render: (row) => {
        const status = {
          "01": "Gerçekleşti",
          "02": "Gönderildi",
          "03": "Gerçekleşmedi",
          "04": "Onayda bekliyor",
          "05": "İşleme Alındı",
        };

        return status[row.paymentStatus] || "Gerçekleşmedi";
      },
    },

    {
      title: "Detay",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (data, row) => (
        <a onClick={() => getDetailModalData(data)}>
          <InfoCircleOutlined style={{ fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "Tekrarla",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (data, row) => {
        return row.consentStatus === "I" ? (
          ""
        ) : (
          <a onClick={() => getAddFormData(data)}>
            <RedoOutlined style={{ fontSize: "130%" }} />
          </a>
        );
      },
    },
  ];

  const detailColumns = [
    // {
    //   title: "İşlem Masraf Para Birimi",
    //   dataIndex: "bankCostCurrencyCode",
    //   render: data => data ?? "-",
    // },
    // {
    //   title: "İşlem Masraf Tutarı",
    //   dataIndex: "bankCostAmount",
    //   render: data => data ? ColumnHelper.renderDecimal(data) : "-",
    // },
    {
      title: "Ödeme Sistemi",
      dataIndex: "paymentSystem",
      render: (type) => {
        const status = {
          H: "Havale",
          F: "FAST",
          E: "EFT (PÖS)",
        };

        return status[type];
      },
    },
    {
      title: "Ödeme Amacı",
      dataIndex: "paymentPurpose",
      render: getPaymentPurpose,
    },
    {
      title: "Açıklama",
      dataIndex: "description",
    },
  ];

  const width = window.innerWidth;
  const breakpoint = 480;
  const textRightClass = width < breakpoint ? "" : "gx-text-right";
  return (
    <>
      <Row align="middle">
        <Col xs={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <BarsOutlined /> ÖDEMELER
          </p>
        </Col>
        <Col xs={8}>
          <Row justify="end" align="middle" style={{ paddingRight: 16 }}>
            <Button
              type="primary"
              onClick={() => setAddFormVisibility(true)}
              style={{ marginBottom: 0 }}
            >
              <SendOutlined /> Ödeme Başlat
            </Button>
          </Row>
        </Col>
      </Row>

      <BkmPaymentTransactionTableFilter
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            expandable={{
              expandedRowRender: (row) =>
                RowHelper.renderRowDetail({
                  data: row,
                  columns: detailColumns,
                }),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%", width: "100%" }}
            rowKey="id"
            filterable={true}
            sortable={true}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
            size="small"
          />
        </Col>
      </Row>

      <Drawer
        id="addDrawer"
        title="Ödeme Başlatma"
        width={500}
        onClose={() => {
          setAddFormVisibility(false);
          setAddFormData({});
        }}
        visible={addFormVisibility}
      >
        <BkmPaymentAddForm onSuccess={onAddFormSuccess} data={addFormData} />
      </Drawer>

      <Modal
        id="detailModal"
        title={`${detailModal.bankName} Rıza Bilgisi`}
        visible={detailModal.visible}
        onCancel={() => {
          setDetailModal({ visible: false });
          setSaveAccountData({});
        }}
        onOk={() => {
          setDetailModal({ visible: false });

          if (
            saveAccountData.id &&
            (saveAccountData.saveSender || saveAccountData.saveRecipient)
          ) {
            RequestHandler.post(
              "/BkmPaymentTransaction/SaveSenderRecipientIban",
              saveAccountData,
              false,
            ).then((_) => {
              setSaveAccountData({});
            });
          } else {
            setSaveAccountData({});
          }
        }}
        cancelButtonProps={{ className: "gx-d-none" }}
        width={1000}
        footer={detailModal.bkmConsentResult ? undefined : null}
      >
        <Divider>İzin Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.consentId}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza Geçerlilik Tarihi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {moment(detailModal.accessValidityDate).format("DD.MM.YYYY")}
          </Col>
        </Row>

        <Divider>Kimlik Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Müşteri Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.customerType === "K" ? "Kurumsal" : "Bireysel"}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getIdentityType(detailModal.identityType)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.identityValue}
          </Col>
        </Row>

        {detailModal.customerType === "K" && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik Türü :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {getIdentityType(detailModal.corporateIdentityType)}
              </Col>
            </Row>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik No :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {detailModal.corporateIdentityValue}
              </Col>
            </Row>
          </>
        )}

        <Divider>Gönderen Bilgileri</Divider>

        {detailModal.senderName && (
          <Row>
            <Col
              xs={24}
              lg={8}
              className={`${textRightClass} gx-font-weight-semi-bold`}
            >
              Gönderen Ünvan :{" "}
            </Col>
            <Col xs={24} lg={16}>
              {detailModal.senderName}
            </Col>
          </Row>
        )}

        {detailModal.senderIban && (
          <Row>
            <Col
              xs={24}
              lg={8}
              className={`${textRightClass} gx-font-weight-semi-bold`}
            >
              Gönderen Iban :{" "}
            </Col>
            <Col xs={24} lg={16}>
              {detailModal.senderIban}
            </Col>
          </Row>
        )}

        {detailModal.senderAccountReference && (
          <Row>
            <Col
              xs={24}
              lg={8}
              className={`${textRightClass} gx-font-weight-semi-bold`}
            >
              Gönderen Hesap Referansı :{" "}
            </Col>
            <Col xs={24} lg={16}>
              {detailModal.senderAccountReference}
            </Col>
          </Row>
        )}

        <Divider>Alıcı Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Ünvan :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.recipientName}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Iban :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.recipientIban}
          </Col>
        </Row>

        <Divider>Ödeme Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Para Birimi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.paymentCurrencyCode}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Tutar :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {ColumnHelper.renderDecimal(detailModal.paymentAmount)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Referans Bilgisi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.referenceNumber}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Ödeme Amacı :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getPaymentPurpose(detailModal.paymentPurpose)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Açıklama :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {detailModal.description}
          </Col>
        </Row>

        <Divider />

        {detailModal.bkmConsentResult && (
          <>
            <Row>
              <Col span={24}>
                <p>{detailModal.bkmConsentResult.message}</p>
              </Col>
            </Row>

            {!detailModal.bkmConsentResult.isSenderSaved && (
              <Row>
                <Col span={24}>
                  <Checkbox
                    checked={saveAccountData.saveSender}
                    onChange={(e) =>
                      setSaveAccountData({
                        ...saveAccountData,
                        saveSender: e.target.checked,
                      })
                    }
                  >
                    Gönderen Hesabımı Kaydet
                  </Checkbox>
                </Col>
              </Row>
            )}

            {!detailModal.bkmConsentResult.isRecipientSaved && (
              <Row>
                <Col span={24}>
                  <Checkbox
                    checked={saveAccountData.saveRecipient}
                    onChange={(e) =>
                      setSaveAccountData({
                        ...saveAccountData,
                        saveRecipient: e.target.checked,
                      })
                    }
                  >
                    Alıcı Hesabını Kaydet
                  </Checkbox>
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default BkmPaymentTransaction;
